import { useContext, useEffect, useRef, useState } from 'react'
import * as d3 from 'd3'
import * as _ from 'underscore'
import Tool from './Tool'
import SegmentContext from '@/components/SegmentHelpers/hooks/createContext'
import { modelInputProps } from '@/components/SegmentHelpers/Interfaces'
import {
  CLICKTYPE_ADD,
  COLOR_ADD,
  COLOR_REMOVE,
  SEGTYPE_BOX,
  SEGTYPE_CLICK,
  SEGTYPE_EVERYTHING,
  SEGTYPE_HOVER
} from '@/components/SegmentHelpers/config'

const Stage = () => {
  const [startPos, setStartPos] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0
  })
  const [dragging, setDragging] = useState<boolean>(false)
  const {
    clicks: [clicks, setClicks],
    image: [image],
    segmentType: [segmentType, setSegmentType],
    clickType: [clickType, setClickType],
    everythingMask: [everythingMask, setEverythingMask],
    isEverythingRemoved: [isEverythingRemoved],
    canvasHeight: [canvasHeight],
    canvasWidth: [canvasWidth],
    overCircle: [, setOverCircle]
  } = useContext(SegmentContext)!
  const getClick = (x: number, y: number, width: number, height: number): modelInputProps => {
    return { x, y, width, height, clickType }
  }

  const svgRef = useRef(null)
  const svg = d3.select(svgRef.current)

  const handleCircleClick = (event: MouseEvent) => {
    const circle = d3.select(event.target)
    const id = circle.attr('id')
    // console.log('circle click', id)
    // console.log(everythingMask)
    const tmp = everythingMask.map((mask) => {
      if (mask.id == id) {
        return { ...mask, display: false }
      } else return mask
    })
    circle.remove()
    setEverythingMask(tmp)
    svg.selectAll('*').remove()
  }
  const handleCircleOver = (event: MouseEvent) => {
    // console.log('over')
    const circle = d3.select(event.target)
    const id = circle.attr('id')
    circle.style('fill', 'blue')
    setOverCircle(id)
  }
  const handleCircleOut = (event: MouseEvent) => {
    const circle = d3.select(event.target)
    const _id = circle.attr('id')
    circle.style('fill', 'red')
    setOverCircle(null)
  }
  useEffect(() => {
    if (isEverythingRemoved) {
      // console.log('removing')
      everythingMask.forEach((mask) => {
        if (mask['display'] && image) {
          const x = mask['point_coords'][0]['x']
          const y = mask['point_coords'][0]['y']
          svg
            .append('circle')
            .attr('cx', (x / image.width) * canvasWidth)
            .attr('cy', (y / image.height) * canvasHeight)
            .attr('r', 5)
            .attr('id', mask['id'])
            .style('fill', 'red')
            .style('position', 'abolute')
            .on('click', handleCircleClick)
            .on('mouseover', handleCircleOver)
            .on('mouseout', handleCircleOut)
        }
      })
    } else {
      svg.selectAll('*').remove()
    }
  }, [isEverythingRemoved, everythingMask])
  // Get mouse position and scale the (x, y) coordinates back to the natural
  // scale of the image. Update the state of clicks with setClicks to trigger
  // the ONNX model to run and generate a new mask via a useEffect in Display.tsx
  const handleMouseDown = (e: any) => {
    console.log('MouseDown')
    if (segmentType == SEGTYPE_BOX) {
      const el = e.nativeEvent.target
      const rect = el.getBoundingClientRect()
      let x = e.clientX - rect.left
      let y = e.clientY - rect.top
      const imageScale = image ? image.width / el.offsetWidth : 1
      x *= imageScale
      y *= imageScale
      setDragging(true)
      setStartPos({
        x: x,
        y: y
      })
      svg
        .append('rect')
        .attr('width', 0)
        .attr('height', 0)
        .attr('x', 0)
        .attr('y', 0)
        .attr('fill', 'none')
        .attr('stroke', 'white')
        .attr('stroke-width', '1px')
        .attr('id', 'selection-rect')
    }
  }
  const handleMouseMove = _.throttle((e: any) => {
    const el = e.nativeEvent.target
    const rect = el.getBoundingClientRect()
    let x = e.clientX - rect.left
    let y = e.clientY - rect.top
    const imageScale = image ? image.width / el.offsetWidth : 1
    x *= imageScale
    y *= imageScale
    if (dragging) {
      const width = Math.abs(startPos.x - x)
      const height = Math.abs(startPos.y - y)
      const xx = startPos.x ? startPos.x : x
      const yy = startPos.y ? startPos.y : y
      const _click = getClick(xx, yy, width, height)
      // dispatch(setClicks([click]))
      // setClicks([click])
      const selectionReact = svg.select('#selection-rect')
      selectionReact
        .attr('x', xx / imageScale)
        .attr('y', yy / imageScale)
        .attr('width', width / imageScale)
        .attr('height', height / imageScale)
    } else {
      const click = getClick(x, y, 0, 0)
      if (segmentType == SEGTYPE_HOVER) {
        // dispatch(setClicks([click]))
        setClicks([click])
      }
    }
  }, 100)
  const handleMouseUp = (e: any) => {
    if (dragging) {
      const el = e.nativeEvent.target
      const rect = el.getBoundingClientRect()
      let x = e.clientX - rect.left
      let y = e.clientY - rect.top
      const imageScale = image ? image.width / el.offsetWidth : 1
      x *= imageScale
      y *= imageScale
      const width = Math.abs(startPos.x - x)
      const height = Math.abs(startPos.y - y)
      // console.log(`x:${x},y:${y}`)
      const click = getClick(startPos.x, startPos.y, width, height)
      // console.log('mouseUp called')
      setTimeout(() => {
        setDragging(false)
        setClickType(CLICKTYPE_ADD)
        setSegmentType(SEGTYPE_CLICK)
        // dispatch(setClicks([click]))
        setClicks([click])
      }, 0)
    }
  }
  const handleClick = (e: any) => {
    if (segmentType != SEGTYPE_EVERYTHING) {
      // console.log('click called')
      let tmp_clicks = clicks
      if (segmentType != SEGTYPE_CLICK) {
        setSegmentType(SEGTYPE_CLICK)
        tmp_clicks = []
      }
      const el = e.nativeEvent.target
      const rect = el.getBoundingClientRect()
      let x = e.clientX - rect.left
      let y = e.clientY - rect.top
      const imageScale = image ? image.width / el.offsetWidth : 1
      x *= imageScale
      y *= imageScale
      const click = getClick(x, y, 0, 0)
      // console.log(click)
      // if (!clicks) clicks = []
      // dispatch(setClicks([...tmp_clicks, click]))
      setClicks([...tmp_clicks, click])
      svg
        .append('circle')
        .attr('cx', x / imageScale)
        .attr('cy', y / imageScale)
        .attr('r', 5)
        .style('fill', clickType == CLICKTYPE_ADD ? COLOR_ADD : COLOR_REMOVE)
        .style('position', 'abolute')
      const _imgContainer = document.getElementById('imgContainer')?.getBoundingClientRect()
    }
  }
  return (
    <>
      <Tool
        handleMouseDown={handleMouseDown}
        handleMouseMove={handleMouseMove}
        handleMouseUp={handleMouseUp}
        handleClick={handleClick}
        svgRef={svgRef}
        isDragging={dragging}
      />
    </>
  )
}
export default Stage
